export const SESSION_ID_ELLIPSIS = '...';
export const MIN_SESSION_ID_EDGE_LENGTH = 1;
export const DEFAULT_SESSION_ID_COLUMN_WIDTH = 200;
export const SESSION_ID_ACCESSOR = 'id';
export const SESSION_ID_COPY_BUTTON_WIDTH = 24;
export const SESSION_ID_GAP_WIDTH = 8;
export const SESSION_ID_MEASURE_SAMPLE = '00000000-0000-0000-0000-000000000000';
const SESSION_ID_VISIBLE_EDGES_COUNT = 2;
const PIXEL_WIDTH_PATTERN = /^(\d+(?:\.\d+)?)(?:px)?$/;

export const getSessionIdColumnWidthValue = (
    width: string | number | undefined
) => {
    if (typeof width === 'number') {
        return width;
    }

    if (typeof width !== 'string') {
        return DEFAULT_SESSION_ID_COLUMN_WIDTH;
    }

    const matchedWidth = PIXEL_WIDTH_PATTERN.exec(width.trim());

    if (!matchedWidth) {
        return DEFAULT_SESSION_ID_COLUMN_WIDTH;
    }

    return Number(matchedWidth[1]);
};

export const getFullSessionIdVisibilityEdgeLength = (sessionId: string) => {
    return Math.ceil(
        (sessionId.length - SESSION_ID_ELLIPSIS.length) /
            SESSION_ID_VISIBLE_EDGES_COUNT
    );
};

export const buildMiddleTruncatedSessionId = (
    sessionId: string,
    edgeLength: number
) => {
    const safeEdgeLength = Math.max(MIN_SESSION_ID_EDGE_LENGTH, edgeLength);

    if (
        sessionId.length <=
        safeEdgeLength * SESSION_ID_VISIBLE_EDGES_COUNT +
            SESSION_ID_ELLIPSIS.length
    ) {
        return sessionId;
    }

    return `${sessionId.slice(0, safeEdgeLength)}${SESSION_ID_ELLIPSIS}${sessionId.slice(-safeEdgeLength)}`;
};

interface GetResponsiveMiddleTruncatedSessionIdParams {
    sessionId: string;
    maxWidth: number;
    measureText: (value: string) => number;
    minEdgeLength?: number;
}

export const getResponsiveMiddleTruncationEdgeLength = ({
    sessionId,
    maxWidth,
    measureText,
    minEdgeLength = MIN_SESSION_ID_EDGE_LENGTH
}: GetResponsiveMiddleTruncatedSessionIdParams) => {
    const fullVisibilityEdgeLength =
        getFullSessionIdVisibilityEdgeLength(sessionId);

    if (measureText(sessionId) <= maxWidth) {
        return fullVisibilityEdgeLength;
    }

    let low = minEdgeLength;
    let high = Math.max(
        minEdgeLength,
        Math.floor(
            (sessionId.length - SESSION_ID_ELLIPSIS.length) /
                SESSION_ID_VISIBLE_EDGES_COUNT
        )
    );
    let bestMatch = minEdgeLength;

    while (low <= high) {
        const edgeLength = Math.floor(
            (low + high) / SESSION_ID_VISIBLE_EDGES_COUNT
        );
        const candidate = buildMiddleTruncatedSessionId(sessionId, edgeLength);

        if (measureText(candidate) <= maxWidth) {
            bestMatch = edgeLength;
            low = edgeLength + 1;
        } else {
            high = edgeLength - 1;
        }
    }

    return bestMatch;
};

export const getResponsiveMiddleTruncatedSessionId = ({
    sessionId,
    maxWidth,
    measureText,
    minEdgeLength = MIN_SESSION_ID_EDGE_LENGTH
}: GetResponsiveMiddleTruncatedSessionIdParams) => {
    const edgeLength = getResponsiveMiddleTruncationEdgeLength({
        sessionId,
        maxWidth,
        measureText,
        minEdgeLength
    });

    return buildMiddleTruncatedSessionId(sessionId, edgeLength);
};
